/**
 * Image preloading utility for critical and background images
 * - Critical: immediate load for hero/above-the-fold
 * - Background: load when browser is idle so it doesn't block rendering
 * - Hover / mousedown: immediate load on link hover or mousedown so the next page feels instant
 */

import { BACKGROUND_PRELOAD_CONCURRENCY } from '../config/preloadConfig';

const preloadedUrls = new Set();

const scheduleIdle = typeof requestIdleCallback !== 'undefined'
  ? requestIdleCallback
  : (cb) => setTimeout(cb, 1);

/**
 * Preload a single image
 * @param {string} src - Image source URL
 * @param {{ fetchPriority?: 'high' | 'low' | 'auto' }} options - fetchPriority for critical/hero images
 * @returns {Promise} Resolves when image is loaded
 */
export const preloadImage = (src, options = {}) => {
  return new Promise((resolve, reject) => {
    if (!src) {
      reject(new Error('No image source provided'));
      return;
    }

    const img = new Image();
    if (options.fetchPriority && img.fetchPriority !== undefined) {
      img.fetchPriority = options.fetchPriority;
    }
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Failed to load image: ${src}`));
    img.src = src;
  });
};

/**
 * Preload multiple images
 * @param {string[]} sources - Array of image source URLs
 * @returns {Promise} Resolves when all images are loaded
 */
export const preloadImages = (sources) => {
  return Promise.all(sources.map(src => preloadImage(src)));
};

/**
 * Preload critical images for a page immediately
 * Call this in useEffect for pages with hero images
 * Skips URLs already preloaded (e.g. from hover or background) to avoid duplicate requests.
 * Uses fetchPriority='high' for hero/above-the-fold images.
 * @param {string[]} criticalImages - Array of critical image URLs
 */
export const preloadCriticalImages = (criticalImages) => {
  if (!criticalImages || criticalImages.length === 0) return;
  const unique = [...new Set(criticalImages)].filter(Boolean);
  const toLoad = unique.filter((url) => !preloadedUrls.has(url));
  toLoad.forEach((url) => preloadedUrls.add(url));
  if (toLoad.length === 0) return;
  Promise.all(toLoad.map((url) => preloadImage(url, { fetchPriority: 'high' }))).catch((err) => {
    console.warn('Some critical images failed to preload:', err);
  });
};

/**
 * Preload images in the background when the browser is idle.
 * Does not block rendering or compete with critical requests.
 * @param {string[]} urls - Array of image URLs to preload
 * @param {{ concurrency?: number }} options - concurrency: max images loading at once (default 3)
 */
export const preloadImagesInBackground = (urls, options = {}) => {
  if (!urls || urls.length === 0) return;
  const concurrency = Math.max(1, options.concurrency ?? BACKGROUND_PRELOAD_CONCURRENCY);
  const toLoad = [...new Set(urls)].filter(Boolean).filter((url) => !preloadedUrls.has(url));
  toLoad.forEach((url) => preloadedUrls.add(url));
  if (toLoad.length === 0) return;

  scheduleIdle(() => {
    let index = 0;
    const run = () => {
      const batch = toLoad.slice(index, index + concurrency);
      index += concurrency;
      if (batch.length === 0) return;
      Promise.all(batch.map((src) => preloadImage(src).catch(() => { }))).finally(() => {
        if (index < toLoad.length) scheduleIdle(run);
      });
    };
    run();
  });
};

/**
 * Preload images for a solutions path (section or subsection page).
 * Use on link hover/focus so the target page's hero loads before navigation.
 * @param {string} path - e.g. "/solutions/industrial-equipment" or "/solutions/industrial-equipment/pump-systems"
 * @param {object} solutions - solutions content from useContent()
 */
export const preloadImagesForSolutionsPath = (path, solutions) => {
  if (!path || !path.startsWith('/solutions/')) return;
  const parts = path.split('/').filter(Boolean);
  if (parts.length < 2 || parts[0] !== 'solutions') return;
  const sectionId = parts[1];
  const section = solutions?.sections?.[sectionId];
  if (!section) return;
  if (parts.length >= 3) {
    const subsectionId = parts[2];
    const subsection = section.subsections?.[subsectionId];
    if (subsection) {
      const urls = getSubsectionImageUrls(subsection, section);
      if (urls.length) preloadCriticalImages(urls);
    }
  } else if (section.heroImage) {
    preloadCriticalImages([section.heroImage]);
  }
};

/**
 * Preload images for a subsection (hero + sections). Use when user hovers a link or for background preload.
 * @param {object} subsection - subsection from content (has images[], sections[])
 * @param {object} section - parent section (for heroImage fallback)
 * @returns {string[]} image URLs to preload
 */
export const getSubsectionImageUrls = (subsection, section) => {
  const out = [];
  const images = subsection?.images || [];
  const hero = images[0] || section?.heroImage;
  if (hero) out.push(hero);
  (subsection?.sections || []).forEach((sec) => {
    const img = sec?.image || sec?.src;
    if (img) out.push(img);
    (sec?.subsections || []).forEach((sub) => {
      if (sub?.image) out.push(sub.image);
    });
  });
  return out;
};

/**
 * Create handlers for preload on hover, focus, and mousedown.
 * Mousedown starts loading before click for faster perceived navigation.
 * @param {() => void} preloadFn - Function to call for preload (e.g. () => preloadImagesForSolutionsPath(path, solutions))
 * @returns {{ onMouseEnter: () => void, onMouseDown: () => void, onFocus: () => void }}
 */
export const createPreloadHandlers = (preloadFn) => ({
  onMouseEnter: preloadFn,
  onMouseDown: preloadFn,
  onFocus: preloadFn,
});

/**
 * Get preload handlers for a solutions path. Use with createPreloadHandlers for hover + mousedown.
 */
export const getPreloadHandlersForPath = (path, solutions) =>
  createPreloadHandlers(() => preloadImagesForSolutionsPath(path, solutions));

const imagePreloader = {
  preloadImage,
  preloadImages,
  preloadCriticalImages,
  preloadImagesInBackground,
  preloadImagesForSolutionsPath,
  getSubsectionImageUrls,
  createPreloadHandlers,
  getPreloadHandlersForPath,
};

export default imagePreloader;

